import * as Sentry from "@sentry/react";
import { identifyUser, resetUser } from "../analytics";
import { reportApiSuccess, reportApiFailure } from "../connectivity";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

export class ApiError extends Error {
  readonly status: number;
  readonly details?: unknown;
  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

function getCsrfToken(): string | null {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === "palnia_csrf") {
      return decodeURIComponent(value);
    }
  }
  return null;
}


export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...options.headers as Record<string, string>,
  };

  const method = (options.method ?? "GET").toUpperCase();
  if (method !== "GET" && method !== "HEAD" && method !== "OPTIONS") {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...options,
      credentials: "include" as const,
      headers,
    });
  } catch (err) {
    reportApiFailure();
    throw err;
  }

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));

    // Compte suspendu → déconnecter et rediriger vers login avec message
    if (response.status === 403 && error.error === "Compte suspendu") {
      clearUserCache();
      localStorage.setItem("palnia_suspended", "true");
      window.location.href = "/login";
      throw new ApiError("Compte suspendu", 403);
    }

    const apiError = new ApiError(
      error.details
        ? `${error.error}: ${JSON.stringify(error.details)}`
        : (error.error ?? `API error: ${response.status}`),
      response.status,
      error.details,
    );
    if (response.status >= 500) {
      reportApiFailure();
      Sentry.captureException(apiError, { extra: { path, status: response.status } });
    } else {
      reportApiSuccess();
    }
    throw apiError;
  }

  reportApiSuccess();
  if (response.status === 204) return undefined as T;
  return response.json();
}

export async function apiUpload<T>(
  path: string,
  formData: FormData,
): Promise<T> {
  const csrfToken = getCsrfToken();
  const response = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include" as const,
    body: formData,
    ...(csrfToken ? { headers: { "X-CSRF-Token": csrfToken } } : {}),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));

    if (response.status === 403 && error.error === "Compte suspendu") {
      clearUserCache();
      window.location.href = "/";
      throw new ApiError("Compte suspendu", 403);
    }

    const apiError = new ApiError(
      error.details
        ? `${error.error}: ${JSON.stringify(error.details)}`
        : (error.error ?? `API error: ${response.status}`),
      response.status,
      error.details,
    );
    if (response.status >= 500) {
      Sentry.captureException(apiError, { extra: { path, status: response.status } });
    }
    throw apiError;
  }

  if (response.status === 204) return undefined as T;
  return response.json();
}

export async function apiFetchBlob(path: string): Promise<Blob> {
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include" as const,
  });

  if (!response.ok) {
    if (response.status === 403) {
      const error = await response.json().catch(() => ({ error: "" }));
      if (error.error === "Compte suspendu") {
        clearUserCache();
        localStorage.setItem("palnia_suspended", "true");
        window.location.href = "/login";
        throw new ApiError("Compte suspendu", 403);
      }
    }
    throw new ApiError(`Image fetch failed: ${response.status}`, response.status);
  }

  return response.blob();
}

export function hasToken(): boolean {
  return !!localStorage.getItem("palnia_user_id");
}

// Keep for backward compat during migration
export function hasUserId(): boolean {
  return hasToken();
}

const USER_DATA_KEYS = [
  "palnia_tasks",
  "palnia_events",
  "palnia_habits",
  "palnia_daily_checks",
  "palnia_custom_tags",
  "palnia_objectives",
  "palnia_pomodoro",
  "palnia_twenty",
];

const USER_PREFS_KEYS = [
  "palnia_tutorial_completed",
  "palnia_recovery_key_confirmed",
];

function clearUserData(includePrefs = false) {
  for (const key of USER_DATA_KEYS) {
    localStorage.removeItem(key);
  }
  if (includePrefs) {
    for (const key of USER_PREFS_KEYS) {
      localStorage.removeItem(key);
    }
  }
}

export function setUserCache(userId: string, role?: string) {
  const previous = localStorage.getItem("palnia_user_id");
  if (previous && previous !== userId) {
    clearUserData(true);
  }
  localStorage.setItem("palnia_user_id", userId);
  if (role) localStorage.setItem("palnia_role", role);
  identifyUser(userId, role ? { role } : undefined);
  Sentry.setUser({ id: userId });
}

// Keep for compat
export function setAuth(userId: string, _token: string, role?: string) {
  setUserCache(userId, role);
}

export function getUserRole(): string {
  return localStorage.getItem("palnia_role") ?? "USER";
}

// Keep for compat
export function setUserId(id: string) {
  setUserCache(id);
}

export function clearUserCache() {
  clearUserData();
  localStorage.removeItem("palnia_user_id");
  localStorage.removeItem("palnia_role");
  resetUser();
  Sentry.setUser(null);
}

// Keep for compat
export function clearUserId() {
  clearUserCache();
}
